'use client';
import { useEffect, useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import SectionHeader from '@/components/ui/SectionHeader';
import Badge from '@/components/ui/Badge';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Button from '@/components/ui/Button';

interface BottleneckItem {
  id: string;
  category: string;
  title: string;
  before: string;
  after: string;
  icon: string;
}

interface SprintArea {
  id: string;
  area: string;
  description: string;
  examples: string[];
}

interface ProcessStep {
  id: string;
  step: string;
  title: string;
  description: string;
}

export default function CaseStudiesPage() {
  const [activeCategory, setActiveCategory] = useState<string>('All');

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, [activeCategory]);

  const categories = ['All', 'Positioning', 'Operations', 'Marketing', 'Automation', 'Offer Clarity'];

  const bottlenecks: BottleneckItem[] = [
    {
      id: 'bn_1',
      category: 'Positioning',
      title: 'Unclear Offer Messaging',
      before: 'Homepage speaks to everyone — no clear ICP, no specific pain addressed, no reason to act now.',
      after: 'Rewritten hero and subheads targeting a specific buyer profile, with pain-point language that converts.',
      icon: 'MegaphoneIcon'
    },
    {
      id: 'bn_2',
      category: 'Operations',
      title: 'Manual Follow-Up Gaps',
      before: 'Leads come in but fall through the cracks. No automated follow-up sequence. Revenue leaks silently.',
      after: 'Automated follow-up system built — triggered by form submission, inquiry, or inbound signal.',
      icon: 'ArrowPathIcon'
    },
    {
      id: 'bn_3',
      category: 'Marketing',
      title: 'Reactive Content with No System',
      before: 'Content posted when someone remembers. No calendar, no repurposing, no compounding distribution.',
      after: 'Content system built with repeatable weekly cadence, repurposing workflow, and platform-specific distribution.',
      icon: 'DocumentTextIcon'
    },
    {
      id: 'bn_4',
      category: 'Automation',
      title: 'Processes Living in People\'s Heads',
      before: 'Handoffs break. The same questions get answered repeatedly. Onboarding is inconsistent.',
      after: 'SOPs documented, workflows automated, and internal tools built to reduce dependency on manual effort.',
      icon: 'CpuChipIcon'
    },
    {
      id: 'bn_5',
      category: 'Offer Clarity',
      title: 'Pricing That Doesn\'t Reflect Value',
      before: 'Pricing page lists features, not outcomes. Prospects don\'t understand what they\'re buying or why it\'s worth it.',
      after: 'Offer reframed around business outcomes. Pricing architecture redesigned to reflect scope, speed, and value.',
      icon: 'CurrencyDollarIcon'
    },
    {
      id: 'bn_6',
      category: 'Marketing',
      title: 'Paid Media Without a Conversion System',
      before: 'Ads running to a homepage with no clear CTA, no follow-up, and no tracking. Budget burning with no attribution.',
      after: 'Dedicated landing page built, conversion tracking implemented, follow-up sequence connected.',
      icon: 'ChartBarIcon'
    },
    {
      id: 'bn_7',
      category: 'Positioning',
      title: 'Service Menu That Dilutes Positioning',
      before: 'Too many services listed. Prospects can\'t tell what the company is actually best at. Trust erodes.',
      after: 'Services restructured into strategic categories that support the core offer and signal expertise.',
      icon: 'ListBulletIcon'
    },
    {
      id: 'bn_8',
      category: 'Automation',
      title: 'No Visibility Into What\'s Working',
      before: 'No reporting system. No dashboard. Decisions made on gut feel with no data to back them up.',
      after: 'Reporting framework built — tracking the metrics that matter for the specific business model.',
      icon: 'PresentationChartLineIcon'
    },
    {
      id: 'bn_9',
      category: 'Operations',
      title: 'Founder as the Bottleneck',
      before: 'Every decision runs through the founder. Nothing ships without approval. Growth is capped at one person\'s bandwidth.',
      after: 'Decision frameworks documented, delegation systems built, and execution handed off with clear accountability.',
      icon: 'UserIcon'
    }
  ];

  const sprintAreas: SprintArea[] = [
    {
      id: 'sprint_1',
      area: 'Offer & Positioning',
      description: 'A sprint focused here typically surfaces misalignment between what the business does and how it\'s communicated to buyers.',
      examples: [
        'Homepage messaging rewritten for a specific ICP',
        'Offer stack restructured around outcomes, not features',
        'Pricing page redesigned to reflect value, not time',
        'Service architecture simplified and repositioned'
      ]
    },
    {
      id: 'sprint_2',
      area: 'Revenue Leak Identification',
      description: 'Most businesses have revenue leaking in predictable places. A sprint surfaces where and builds the fix.',
      examples: [
        'Follow-up gaps identified and automated',
        'Abandoned inquiry sequences built',
        'Upsell and cross-sell paths mapped and implemented',
        'Lead capture improved on high-traffic pages'
      ]
    },
    {
      id: 'sprint_3',
      area: 'AI & Automation Buildout',
      description: 'A sprint here reduces manual work and builds systems that run without constant founder involvement.',
      examples: [
        'AI-assisted content workflows built',
        'Internal tools and dashboards created',
        'Repetitive processes automated end-to-end',
        'Chrome extensions or custom tools developed'
      ]
    },
    {
      id: 'sprint_4',
      area: 'Content & Distribution',
      description: 'A sprint here turns expertise into a repeatable content system that compounds over time.',
      examples: [
        'Content calendar and repurposing system built',
        'Platform-specific distribution workflows created',
        'Existing content audited and repositioned',
        'X, Afterhour, and owned channel strategy aligned'
      ]
    },
    {
      id: 'sprint_5',
      area: 'Web & Funnel Clarity',
      description: 'A sprint here tightens the conversion path from first visit to booked call or purchase.',
      examples: [
        'Landing pages rebuilt with clear ICP targeting',
        'Conversion tracking and attribution implemented',
        'CTA hierarchy simplified across key pages',
        'Site messaging aligned with current offer'
      ]
    },
    {
      id: 'sprint_6',
      area: 'Operations & Systems',
      description: 'A sprint here documents what\'s in people\'s heads and builds the infrastructure for consistent execution.',
      examples: [
        'SOPs written for core repeatable processes',
        'Onboarding and handoff workflows documented',
        'Reporting framework built for key metrics',
        'Team accountability systems implemented'
      ]
    }
  ];

  const processSteps: ProcessStep[] = [
    {
      id: 'ps_1',
      step: '01',
      title: 'Diagnostic Audit',
      description: 'We start by mapping the business — offer, positioning, marketing, operations, and follow-up. We\'re looking for the real constraint, not the symptom.'
    },
    {
      id: 'ps_2',
      step: '02',
      title: 'Bottleneck Analysis',
      description: 'We identify where revenue is leaking, where execution is breaking down, and where the highest-leverage opportunities are hiding.'
    },
    {
      id: 'ps_3',
      step: '03',
      title: 'Prioritization',
      description: 'Not everything gets fixed at once. We rank opportunities by impact and effort — so the first sprint addresses the highest-value constraint.'
    },
    {
      id: 'ps_4',
      step: '04',
      title: 'Roadmap Creation',
      description: 'A clear, phased execution plan. You know exactly what gets built, in what order, and why. No mystery work.'
    },
    {
      id: 'ps_5',
      step: '05',
      title: 'Weekly Execution',
      description: 'Sprints are planned Monday, executed through the week, reviewed Friday. Consistent rhythm. Real work ships every week.'
    },
    {
      id: 'ps_6',
      step: '06',
      title: 'Reporting & Visibility',
      description: 'You always know what we\'re working on, what shipped, and what\'s next. Transparent capacity planning and progress reporting.'
    },
    {
      id: 'ps_7',
      step: '07',
      title: 'Refinement & Optimization',
      description: 'We iterate based on data. What\'s working gets doubled down. What isn\'t gets adjusted. Execution improves every sprint.'
    }
  ];

  const filteredBottlenecks = activeCategory === 'All'
    ? bottlenecks
    : bottlenecks.filter(b => b.category === activeCategory);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        {/* Hero Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal">
              <SectionHeader
                eyebrow="Proof & Process"
                title="Case Studies — In Development"
                description="We don't have polished agency case studies with invented metrics. What we have is 20+ years of pattern recognition, a real portfolio of built properties, and an honest picture of the bottlenecks we solve. Formal case studies are being documented now."
                align="center"
                className="mb-8"
              />
              <div className="flex flex-wrap justify-center gap-3 mt-8">
                <Badge variant="default">No Fabricated Metrics</Badge>
                <Badge variant="default">No Invented Client Names</Badge>
                <Badge variant="default">Pattern-Based Capability Proof</Badge>
              </div>
              <div className="flex flex-wrap justify-center gap-4 mt-8">
                <Link href="/portfolio">
                  <Button variant="primary" size="md">
                    View Real Portfolio →
                  </Button>
                </Link>
                <Link href="/book">
                  <Button variant="secondary" size="md">
                    Start With a Sprint
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Portfolio as Primary Trust Bridge */}
        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-12">
              <SectionHeader
                eyebrow="Real Portfolio — Primary Proof"
                title="Sites, Systems, and Brands We've Built"
                description="Until formal case studies are published, the portfolio is the primary trust bridge. These are real properties — built, grown, and operated by Best Odds Corp across e-commerce, media, services, and technology."
                align="center"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {[
                {
                  domain: 'StreetSaw.com',
                  url: 'https://streetsaw.com',
                  category: 'E-Commerce · Electronics',
                  summary: 'Electronics brand selling hoverboards, electric scooters, and e-bikes. Built from the ground up — platform, paid media, SEO, and brand development.',
                  tags: ['E-Commerce Platform', 'Paid Media', 'Brand Development', 'Product SEO']
                },
                {
                  domain: 'Organic101.com',
                  url: 'https://organic101.com',
                  category: 'E-Commerce · Wellness',
                  summary: 'Natural bodycare brand selling organic deodorants and luxury lip balms. E-commerce, content marketing, SEO, and brand identity for health-conscious consumers.',
                  tags: ['E-Commerce', 'Content Marketing', 'SEO', 'Brand Development']
                },
                {
                  domain: 'VelvetAngels.com',
                  url: 'https://velvetangels.com',
                  category: 'Brand Revival · Equity Stake',
                  summary: 'Celebrity-worn shoe brand relaunching in 2026. Best Odds Corp holds an active equity stake — involved from pitch deck to asset acquisition to corporate structuring.',
                  tags: ['Pitch Deck', 'Web Design', 'Asset Acquisition', 'Fundraising Advisory']
                },
                {
                  domain: 'HealthcareUnlocked.com',
                  url: 'https://healthcareunlocked.com',
                  category: 'Healthcare · E-Commerce',
                  summary: 'Healthcare company launched at the start of the 2020 COVID pandemic — distributed PPE nationwide. Built the platform and provider onboarding systems under extreme time pressure.',
                  tags: ['Platform Development', 'Healthcare SEO', 'Supply Chain Content', 'Provider Onboarding']
                },
                {
                  domain: 'SkyhookCapital.io',
                  url: 'https://skyhookcapital.io',
                  category: 'Real Estate · Finance',
                  summary: 'Real estate company using creative finance strategies — subject-to, owner finance, wrap-around mortgages. Built platform architecture, financial content, and lead generation.',
                  tags: ['Platform Architecture', 'Financial Content', 'Investor Relations', 'SEO & Lead Gen']
                },
                {
                  domain: 'MacPokerOnline.com',
                  url: 'https://macpokeronline.com',
                  category: 'Niche Media · Since 2005',
                  summary: 'Flagship niche media property built in 2005. Premier destination for Mac users — comprehensive reviews, compatibility guides, and SEO-driven organic growth over 20+ years.',
                  tags: ['SEO Strategy', 'Content Marketing', 'Performance Marketing', 'Technical Development']
                }
              ].map((item) => (
                <div key={item.domain} className="reveal p-8 border border-border hover:border-primary transition-all duration-300 bg-card flex flex-col">
                  <div className="mb-3">
                    <span className="text-micro uppercase tracking-widest text-primary">{item.category}</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{item.domain}</h3>
                  <p className="text-body-sm text-muted-foreground mb-5 flex-1 leading-relaxed">{item.summary}</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {item.tags.map((tag) => (
                      <span key={tag} className="text-micro uppercase tracking-widest text-muted-foreground border border-border px-2 py-1">{tag}</span>
                    ))}
                  </div>
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-hover text-body-sm underline transition-colors">
                    Visit {item.domain} →
                  </a>
                </div>
              ))}
            </div>
            <div className="reveal text-center">
              <Link href="/portfolio">
                <Button variant="secondary" size="lg">
                  View Full Portfolio
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Process Flow Section */}
        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16">
              <SectionHeader
                eyebrow="How We Work"
                title="The Execution Process"
                description="This is not random freelance work. Every engagement follows a systematic process designed to find the constraint, build the plan, and ship execution every week."
                align="center"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {processSteps.slice(0, 4).map((step) => (
                <div key={step.id} className="reveal p-8 border border-border bg-card hover:border-primary transition-colors">
                  <div className="text-4xl font-bold text-primary mb-4 opacity-60">{step.step}</div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {processSteps.slice(4).map((step) => (
                <div key={step.id} className="reveal p-8 border border-border bg-card hover:border-primary transition-colors">
                  <div className="text-4xl font-bold text-primary mb-4 opacity-60">{step.step}</div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottlenecks We Solve */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16">
              <SectionHeader
                eyebrow="Examples of Bottlenecks We Solve"
                title="Before & After: What Changes"
                description="These are the patterns we see repeatedly across founder-led businesses. Not invented case studies — real bottleneck types with honest before/after framing."
                align="center"
              />
            </div>

            {/* Category Filter */}
            <div className="reveal flex flex-wrap gap-3 justify-center mb-12">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 text-micro uppercase tracking-widest font-medium border transition-all ${
                    activeCategory === cat
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-transparent text-muted-foreground border-border hover:border-primary hover:text-primary'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBottlenecks.map((item) => (
                <div key={item.id} className="reveal border border-border hover:border-primary transition-all duration-300 bg-card group">
                  <div className="p-6 border-b border-border">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 border border-border group-hover:border-primary flex items-center justify-center transition-colors">
                        <Icon name={item.icon as any} size={20} className="text-primary" />
                      </div>
                      <Badge variant="default">{item.category}</Badge>
                    </div>
                    <h3 className="text-xl font-semibold">{item.title}</h3>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <span className="text-micro uppercase tracking-ultra-wide text-red-400 block mb-2">Before</span>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{item.before}</p>
                    </div>
                    <div className="border-t border-border pt-4">
                      <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-2">After</span>
                      <p className="text-body-sm text-foreground leading-relaxed">{item.after}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What a Sprint Can Surface */}
        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16">
              <SectionHeader
                eyebrow="What a Sprint Can Surface"
                title="Sample Transformation Areas"
                description="A sprint is a focused engagement — typically 1–4 weeks — that audits a specific area of the business and produces a prioritized action plan with quick wins where possible."
                align="center"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sprintAreas.map((area) => (
                <div key={area.id} className="reveal p-8 border border-border hover:border-primary transition-colors bg-card">
                  <h3 className="text-xl font-semibold mb-3 text-foreground">{area.area}</h3>
                  <p className="text-body-sm text-muted-foreground mb-6 leading-relaxed">{area.description}</p>
                  <ul className="space-y-2">
                    {area.examples.map((example, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-body-sm text-foreground">
                        <span className="text-primary mt-1 flex-shrink-0">→</span>
                        <span>{example}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Outcome Framing Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16">
              <SectionHeader
                eyebrow="What Clients Typically Gain"
                title="Honest Outcome Framing"
                description="We don't use fabricated ROI claims. These are the types of outcomes that emerge from systematic bottleneck work — framed honestly, without invented numbers."
                align="center"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {[
                { icon: 'MagnifyingGlassIcon', outcome: 'Found missed revenue opportunities', detail: 'Identified follow-up gaps, unconverted leads, and under-leveraged assets that were costing the business money.' },
                { icon: 'CpuChipIcon', outcome: 'Reduced manual work', detail: 'Automated repetitive processes, built internal tools, and created systems that run without constant founder involvement.' },
                { icon: 'MegaphoneIcon', outcome: 'Tightened offer clarity', detail: 'Repositioned the offer so the right buyers understand the value immediately — reducing friction in the sales process.' },
                { icon: 'BoltIcon', outcome: 'Accelerated execution speed', detail: 'Built with AI leverage — delivering work faster than traditional agency workflows without sacrificing quality.' },
                { icon: 'DocumentTextIcon', outcome: 'Improved site and message clarity', detail: 'Rewrote homepage, service pages, and CTAs so the business communicates what it does and who it\'s for.' },
                { icon: 'ChartBarIcon', outcome: 'Identified high-value bottlenecks', detail: 'Turned vague "we need to grow" problems into specific, prioritized action plans with clear next steps.' },
              ].map((item, idx) => (
                <div key={idx} className="reveal flex items-start gap-6 p-6 border border-border hover:border-primary transition-colors">
                  <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                    <Icon name={item.icon as any} size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{item.outcome}</h3>
                    <p className="text-body-sm text-muted-foreground">{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Coming Soon — Real Case Studies */}
        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal max-w-3xl mx-auto text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">Coming Soon</span>
              <h2 className="text-4xl font-bold mb-6">Real Mini Case Studies — In Progress</h2>
              <p className="text-body text-muted-foreground mb-8 leading-relaxed">
                We are actively building a library of honest mini case studies — sprint snapshots, before/after positioning improvements, automation buildouts, and execution examples. These will be added as they are completed and approved for publication.
              </p>
              <p className="text-body-sm text-muted-foreground mb-10">
                No invented metrics. No fabricated client names. Just real work, documented honestly.
              </p>
              <Link href="/book">
                <Button variant="primary" size="lg">
                  Start With a Sprint
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal text-center">
              <h2 className="text-4xl font-bold mb-6">Ready to Find Your Bottleneck?</h2>
              <p className="text-body text-muted-foreground mb-8 max-w-2xl mx-auto">
                Book a consultation and we'll identify your biggest constraint, build the execution plan, and start shipping within the week.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/book">
                  <button className="px-8 py-4 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-label uppercase tracking-widest">
                    Book a Sprint
                  </button>
                </Link>
                <Link href="/pricing">
                  <button className="px-8 py-4 border border-border hover:border-primary text-foreground hover:text-primary transition-colors text-label uppercase tracking-widest">
                    See Pricing
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
